import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, X } from "lucide-react";
import { api } from "../../lib/api";

const KIND_LABEL = {
  postcode: "Postcode",
  plaats: "Plaats",
  provincie: "Provincie",
  wijk: "Wijk / buurt",
};

/**
 * LocationAutocomplete
 * - Debounced fetch to /api/locations/suggest
 * - Keyboard nav (ArrowUp/ArrowDown/Enter/Escape)
 * - Emits onSelect({kind, value}) and stays a controlled input via value/onChange.
 */
export const LocationAutocomplete = ({
  value,
  onChange,
  onSelect,
  onSubmit,
  mode = "all",
  placeholder = "Plaats, postcode, wijk of provincie",
  testid = "location-input",
  className = "",
  size = "md",
}) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const reqIdRef = useRef(0);

  // Debounced fetch
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) {
      setItems([]);
      setActive(-1);
      return;
    }
    const t = setTimeout(async () => {
      const rid = ++reqIdRef.current;
      setLoading(true);
      try {
        const d = await api(`/locations/suggest?q=${encodeURIComponent(q)}&mode=${mode}&limit=8`);
        if (rid !== reqIdRef.current) return;
        setItems(d?.suggestions || []);
        setActive(-1);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [value, mode]);

  // Close on outside click, but never intercept a click on a submit button in
  // the same form (that would reflow the button between mousedown and mouseup
  // and swallow the tap, especially on mobile). The submit-listener below
  // handles closing the panel when the form actually submits.
  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target)) return;
      const submitBtn = e.target?.closest?.('button[type="submit"]');
      const ourForm = wrapRef.current.closest("form");
      if (submitBtn && ourForm && submitBtn.form === ourForm) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Also close whenever the parent form submits (guarantees panel dismisses on Zoeken)
  useEffect(() => {
    const form = wrapRef.current?.closest("form");
    if (!form) return;
    const onSub = () => setOpen(false);
    form.addEventListener("submit", onSub);
    return () => form.removeEventListener("submit", onSub);
  }, []);

  const pick = (it) => {
    onSelect?.(it);
    onChange(it.value);
    setOpen(false);
    setItems([]);
    inputRef.current?.blur();
  };

  const handleKey = (e) => {
    if (!open || items.length === 0) {
      if (e.key === "Enter") onSubmit?.();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(-1, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && items[active]) pick(items[active]);
      else onSubmit?.();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const sizes = useMemo(
    () =>
      size === "sm"
        ? { input: "h-10 pl-9 pr-9 text-sm", icon: 16 }
        : { input: "h-12 pl-10 pr-10 text-[15px]", icon: 18 },
    [size]
  );

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <MapPin size={sizes.icon} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        data-testid={testid}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        className={`w-full rounded-full border border-border bg-white text-navy outline-none transition focus:border-brandblue focus:ring-2 focus:ring-brandblue/20 ${sizes.input}`}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="loc-suggestions"
      />
      {value && (
        <button
          type="button"
          data-testid={`${testid}-clear`}
          aria-label="Wis locatie"
          onClick={() => { onChange(""); setItems([]); inputRef.current?.focus(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
        >
          <X size={sizes.icon - 2} />
        </button>
      )}
      {open && (items.length > 0 || loading) && (
        <div
          id="loc-suggestions"
          role="listbox"
          data-testid="location-suggestions"
          className="static mt-1.5 sm:absolute sm:mt-0 sm:left-0 sm:right-0 sm:top-[calc(100%+6px)] z-30 max-h-[320px] overflow-y-auto rounded-2xl border border-border bg-white p-1 shadow-xl"
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">Zoeken…</div>
          )}
          {items.map((it, i) => (
            <button
              key={`${it.kind}-${it.value}`}
              type="button"
              data-testid={`loc-suggest-${i}`}
              onClick={() => pick(it)}
              onMouseEnter={() => setActive(i)}
              className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm ${
                active === i ? "bg-brandblue-light text-brandblue" : "text-navy hover:bg-slate-50"
              }`}
            >
              <Search size={13} className="text-slate-400" />
              <span className="font-semibold">{it.label}</span>
              <span className="ml-auto text-xs text-slate-400">{KIND_LABEL[it.kind] || it.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
